// ─────────────────────────────────────────────────────────────
// Aspect Service, aspect definitions and calculations
// ─────────────────────────────────────────────────────────────

// ─── Aspect definitions ─────────────────────────────────────

export interface AspectDefinition {
  name: string;
  namePt: string;
  angle: number;
  orb: number;
  nature: 'harmonious' | 'challenging' | 'neutral';
  strength: number; // 1–5 (5 = strongest)
  symbol: string;
}

export const ASPECTS: AspectDefinition[] = [
  { name: 'Conjunction',  namePt: 'Conjunção',       angle: 0,   orb: 8, nature: 'neutral',     strength: 5, symbol: '☌' },
  { name: 'Opposition',   namePt: 'Oposição',        angle: 180, orb: 8, nature: 'challenging',  strength: 5, symbol: '☍' },
  { name: 'Trine',        namePt: 'Trígono',         angle: 120, orb: 7, nature: 'harmonious',   strength: 4, symbol: '△' },
  { name: 'Square',       namePt: 'Quadratura',      angle: 90,  orb: 7, nature: 'challenging',  strength: 4, symbol: '□' },
  { name: 'Sextile',      namePt: 'Sextil',          angle: 60,  orb: 5, nature: 'harmonious',   strength: 3, symbol: '⚹' },
  { name: 'Quincunx',     namePt: 'Quincúncio',      angle: 150, orb: 3, nature: 'challenging',  strength: 2, symbol: '⚻' },
  { name: 'Semisquare',   namePt: 'Semiquadratura',  angle: 45,  orb: 2, nature: 'challenging',  strength: 2, symbol: '∠' },
  { name: 'Semisextile',  namePt: 'Semissextil',     angle: 30,  orb: 2, nature: 'harmonious',   strength: 1, symbol: '⊻' },
];

// ─── Aspect calculation ─────────────────────────────────────

export interface FoundAspect {
  angle: number;           // actual angular difference
  aspect: AspectDefinition | null;
  orb: number;             // distance from exact aspect angle
}

/** Find the aspect (if any) between two ecliptic longitudes. */
export function calculateAspect(lon1: number, lon2: number): FoundAspect {
  let diff = Math.abs(lon1 - lon2);
  if (diff > 180) diff = 360 - diff;

  for (const aspect of ASPECTS) {
    const orb = Math.abs(diff - aspect.angle);
    if (orb <= aspect.orb) {
      return { angle: diff, aspect, orb };
    }
  }
  return { angle: diff, aspect: null, orb: 0 };
}

/** Calculate intensity 0–100 based on how tight the orb is. */
export function calculateIntensity(orb: number, maxOrb: number): number {
  return Math.round((1 - orb / maxOrb) * 100);
}

// ─── Aspect colors (for UI rendering) ──────────────────────

export const ASPECT_COLORS: Record<string, string> = {
  Conjunction: '#F5C842',
  Opposition:  '#f87171',
  Trine:       '#4ade80',
  Square:      '#f87171',
  Sextile:     '#60a5fa',
  Quincunx:    '#c084fc',
  Semisquare:  '#fb923c',
  Semisextile: '#a3e635',
};

export const NATURE_COLORS: Record<string, string> = {
  harmonious:  '#4ade80',
  challenging: '#f87171',
  neutral:     '#F5C842',
};
